import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { UserType } from "@app/enum/UserType";
import { IUser } from "@app/interfaces/IUser";
import { IVehicle } from "@app/interfaces/IVehicle";
import { CameraChannelType } from "@app/main/components/videos/enums/CameraChannelType";
import { ICameraChannel } from "@app/main/components/videos/models/ICameraChannel";
import { CameraChannelService } from "@app/main/modules/cameras/_services/camera-channels.service";
import { FoundationPeripheralService } from "@app/main/modules/foundation/_services/peripheral.service.ajs";
import { CurrentUserService } from "@app/services/currentUser.service";
import { VehicleService } from "@app/services/vehicle.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AutoDownloadConfigurationComponent } from "../camera-channels-editor/auto-download-configuration/auto-download-configuration.component";
import './camera-vehicles-manager.component.scss';

export interface IVehicleWithCameraMappings extends IVehicle {
    cameraChannels?: ICameraChannel[];
}

@Component({
    selector: 'camera-vehicles-manager',
    template: require('./camera-vehicles-manager.component.html')
})
export class CameraVehiclesManagerComponent implements OnInit, OnDestroy {
    @ViewChild(AutoDownloadConfigurationComponent) autoDownloadComponent!: AutoDownloadConfigurationComponent;

    vehicles: IVehicleWithCameraMappings[];
    cameraChannels: ICameraChannel[];
    selectedVehicle: IVehicleWithCameraMappings;

    tableColumns: IVTableColumn<IVehicle>[] = [];
    tableRows: IVTableRow[] = [];

    cameraChannelTypes = Object.keys(CameraChannelType).filter(s => !isNaN(Number(s))).map(s => Number(s));

    loading: boolean;

    firstLoad = true;
    isAutoDownloadSelected = true;

    user: IUser;
    get isInternalUser(): boolean {
        return this.user?.Type === UserType.InternalUser;
    }

    private destroy$ = new Subject<void>();
    constructor(
        private readonly cameraChannelService: CameraChannelService,
        private readonly vehicleService: VehicleService,
        private readonly vTableService: VTableService,
        private readonly foundationPeripheralService: FoundationPeripheralService,
        private readonly currentUserService: CurrentUserService
    ) { }

    ngOnInit(): void {
        this.load();
        this.createTable();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }

    selectVehicle(tableRow: IVTableRow): void {
        this.selectedVehicle = tableRow.data;
    }

    clearSelectedVehicle(): void {
        this.selectedVehicle = null;
    }

    vehicleSaved(): void {
        this.createTableRows();
        this.clearSelectedVehicle();
    }

    onTabSelected(value: boolean): void {
        this.isAutoDownloadSelected = value;
    }

    createNewSetting(): void {
        this.autoDownloadComponent.createNewSetting();
    }

    //--

    private load(): void {
        this.loading = true;
        this.getVehicles().then(() => {
            this.getData();
        }).finally(() => {
            this.loading = false;
        });

        this.currentUserService.getCurrentUser().then(currentUser => {            
            this.user = currentUser;            
        });
    }

    private getVehicles(): Promise<void> {
        return this.vehicleService.getVehicles({ includeAllVehicles: true }).then(vehicles => {
            return this.foundationPeripheralService.getPeripheralsAndMapToVehicles(vehicles).then(() => {
                this.vehicles = vehicles.filter(v => this.foundationPeripheralService.isAnyPeripheralCamera(v.peripherals));
            });
        }) as Promise<void>;
    }

    private createTable(): void {
        const cameraChannelTypeColumns: IVTableColumn<IVehicle>[] = [];
        this.cameraChannelTypes.forEach(type => {
            cameraChannelTypeColumns.push({
                header: this.vTableService.createTableHeader({ tKey: `CameraChannelTypeEnum.${CameraChannelType[type]}`, width: 125, sortType: SortType.Number }),
                createCellFunction: (vehicle) => this.getChannelTypeCountCell(vehicle, type)
            })
        })

        this.tableColumns = [
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (vehicle) => this.vTableService.createVehicleRegCell(vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CameraManagement.ActiveChannels", width: 150, sorted: true, sortType: SortType.Number, sortDirection: SortDirection.Descending }),
                createCellFunction: (vehicle) => this.createChannelsCell(vehicle, true)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CameraManagement.InactiveChannels", width: 150 }),
                createCellFunction: (vehicle) => this.createChannelsCell(vehicle, false)
            },
            ...cameraChannelTypeColumns
        ]
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.vehicles, this.tableColumns);
    }

    private getData(): void {
        this.cameraChannelService.getCameraChannels().pipe(takeUntil(this.destroy$)).subscribe(channels => {
            this.cameraChannels = channels;
            this.vehicles.forEach(vehicle => vehicle.cameraChannels = channels.filter(channel => channel.VehicleId === vehicle.Id));
            this.createTableRows();
            this.firstLoad = false;
        })
    }

    private createChannelsCell(vehicle: IVehicleWithCameraMappings, active: boolean): IVTableCell {
        const vehicleChannels = vehicle.cameraChannels?.filter(channel => channel.IsActive === active);
        return this.vTableService.createTableCell(vehicleChannels?.length || 0);
    }

    private getChannelTypeCountCell(vehicle: IVehicleWithCameraMappings, type: CameraChannelType): IVTableCell {
        if (!vehicle.cameraChannels?.length) {
            return this.vTableService.createTableCell(null);
        }
        const channelTypeList = vehicle.cameraChannels.filter(channel => channel.CameraChannelType === type);
        const value = channelTypeList.length || null; // We want table fallback value i.e. N/A when count is 0;
        const classes = channelTypeList.length ? "" : "text-muted";

        return this.vTableService.createTableCell(value, {classes});
    }
}
